import { RuleId, skipToMainContentRule } from '@wix/a11y-rules';
import { useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus } from '@wix/rules-panel-ui';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { ruleApplyTriggeredAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { convertScanStatusToUiStatus } from '../../components/fixers/alt-image-fixer/utils';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { SITE_LEVEL_PAGE_ID } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { PanoramaErrorNames, PanoramaTransactions } from '../../panorama/constants';
import { usePanoramaFunctions } from '../../panorama/use-panorama-functions';
import { getFixerDesignPropsFromCapabilities, getFooterButtonTypesFromCapabilities } from '../../utils/get-fixer-design-props';
import { useRuleBase } from '../shared/use-rule-base';
const ruleChecker = skipToMainContentRule.checker;
export const useSkipToMainContentRule = () => {
  const {
    getRuleState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const setRuleApplyTriggered = useSetAtom(ruleApplyTriggeredAtom);
  const {
    t
  } = useTranslation();
  const {
    a11yWizardAdapter,
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    setSkipToMainContent
  } = a11yWizardAdapter;
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const {
    finishPanoramaTransaction,
    startPanoramaTransaction,
    reportErrorPanorama
  } = usePanoramaFunctions();
  const getSkipToMainContentProps = useCallback(_ref => {
    var _ruleState$state;
    let {
      ruleExecutionResult
    } = _ref;
    const {
      result,
      ruleId
    } = ruleExecutionResult;
    const {
      status
    } = result;
    const ruleState = getRuleState(SITE_LEVEL_PAGE_ID, ruleId);
    const uiStatus = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.status) ?? convertScanStatusToUiStatus(status);
    return {
      dataHook: `${SITE_LEVEL_PAGE_ID}-${ruleId}`,
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: uiStatus !== RuleListStatus.failed ? 'accessibility-wizard.rules-list.skip-to-main-content.description' : 'accessibility-wizard.rules-list.skip-to-main-content.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-skip-to-main-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount: 1,
            failedElementCount: Number(status === 'failed')
          });
        },
        textButtonAriaLabel: t('accessibility-wizard.rules-list.skip-to-main-content.learn-more.aria-label')
      }),
      fixer: FixerType.ToggleRule,
      fixerProps: {
        completedRuleContent: {
          title: t('accessibility-wizard.rules-list.issue-resolved')
        },
        design: getFixerDesignPropsFromCapabilities(capabilities),
        footerLabels: {
          buttonTypes: getFooterButtonTypesFromCapabilities(capabilities),
          dismiss: t('accessibility-wizard.rules-list.skip-to-main-content.footer-button.cancel'),
          submit: t('accessibility-wizard.rules-list.skip-to-main-content.footer-button.apply')
        },
        onApply: async checked => {
          startPanoramaTransaction(PanoramaTransactions.skipToMainContentRule);
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Apply,
            index: 0,
            status: uiStatus
          });
          biLogger.taskElementApplyClick({
            index: 0,
            status: uiStatus,
            value: checked.toString()
          });
          try {
            setSkipToMainContent(true);
          } catch (e) {
            reportErrorPanorama(e, PanoramaErrorNames.skipToMainContent);
            return;
          }
          updateRuleStateAndVerifyIfNeeded(SITE_LEVEL_PAGE_ID, ruleId, {
            enabled: true,
            status: convertScanStatusToUiStatus(ruleChecker({
              skipToMainContentEnabled: true
            }).status)
          });
          setIsApplyTriggered(true);
          setRuleApplyTriggered({
            pageId: SITE_LEVEL_PAGE_ID,
            ruleId: RuleId.SKIP_TO_MAIN_CONTENT
          });
          finishPanoramaTransaction(PanoramaTransactions.skipToMainContentRule);
        },
        onCancel: () => {
          biLogger.taskElementCtaClick({
            ctaName: BiCtaNames.Cancel,
            index: 0,
            status: uiStatus
          });
        },
        onSelect: selectedValue => {
          biLogger.elementFieldChange({
            index: 0,
            status: uiStatus,
            value: selectedValue.toString()
          });
        },
        toggleLabel: t('accessibility-wizard.rules-list.skip-to-main-content.toggle-label')
      },
      id: ruleId,
      isEditableOnceCompleted: false,
      label: t('accessibility-wizard.rules-list.skip-to-main-content.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: convertScanStatusToUiStatus(status),
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          status: uiStatus
        });
        biLogger.elementStatusChange({
          index: 0,
          status: uiStatus
        });
      },
      parentGroupId: SITE_LEVEL_PAGE_ID,
      ruleId,
      status: uiStatus
    };
  }, [capabilities, getRuleState, t, editorHost, openSelectedArticle, biLogger, startPanoramaTransaction, updateRuleStateAndVerifyIfNeeded, setIsApplyTriggered, setRuleApplyTriggered, finishPanoramaTransaction, setSkipToMainContent, reportErrorPanorama, setCurrentTaskInfo, currentTaskInfo]);
  return {
    getSkipToMainContentProps
  };
};
//# sourceMappingURL=use-skip-to-main-content-rule.js.map